import { Store } from 'react-notifications-component';
import {
  get,
  post,
  put,
  remove,
} from '@api';
import {
  COMMON_DATA_DELETE_FAIL,
  COMMON_DATA_DELETE_SUCCESS,
  COMMON_DATA_UPDATE_FAIL,
  COMMON_DATA_UPDATE_SUCCESS,
} from '@constants/notification-settings';

/**
 * Redux action types для модуля Filters.
 * Группировка по фазам жизни фильтра (list / item CRUD / items CRUD / custom items CRUD).
 */
export const FETCH_FILTERS_START = 'FETCH_FILTERS_START';
export const FETCH_FILTERS_SUCCESS = 'FETCH_FILTERS_SUCCESS';
export const FETCH_FILTERS_FAIL = 'FETCH_FILTERS_FAIL';

export const CREATE_FILTER_START = 'CREATE_FILTER_START';
export const CREATE_FILTER_SUCCESS = 'CREATE_FILTER_SUCCESS';
export const CREATE_FILTER_FAIL = 'CREATE_FILTER_FAIL';

export const DELETE_FILTER_START = 'DELETE_FILTER_START';
export const DELETE_FILTER_SUCCESS = 'DELETE_FILTER_SUCCESS';
export const DELETE_FILTER_FAIL = 'DELETE_FILTER_FAIL';

export const FETCH_FILTER_ITEM_START = 'FETCH_FILTER_ITEM_START';
export const FETCH_FILTER_ITEM_SUCCESS = 'FETCH_FILTER_ITEM_SUCCESS';
export const FETCH_FILTER_ITEM_FAIL = 'FETCH_FILTER_ITEM_FAIL';

export const UPDATE_FILTER_START = 'UPDATE_FILTER_START';
export const UPDATE_FILTER_SUCCESS = 'UPDATE_FILTER_SUCCESS';
export const UPDATE_FILTER_FAIL = 'UPDATE_FILTER_FAIL';

export const ADD_FILTER_ITEMS_SUCCESS = 'ADD_FILTER_ITEMS_SUCCESS';
export const REMOVE_FILTER_ITEM_SUCCESS = 'REMOVE_FILTER_ITEM_SUCCESS';
export const UPDATE_FILTER_ITEM_POSITION_SUCCESS = 'UPDATE_FILTER_ITEM_POSITION_SUCCESS';
export const REPLACE_FILTER_ATTRIBUTE_ITEMS_SUCCESS = 'REPLACE_FILTER_ATTRIBUTE_ITEMS_SUCCESS';
export const UPSERT_FILTER_ATTRIBUTE_CONFIG_SUCCESS = 'UPSERT_FILTER_ATTRIBUTE_CONFIG_SUCCESS';
export const DELETE_FILTER_ATTRIBUTE_CONFIG_SUCCESS = 'DELETE_FILTER_ATTRIBUTE_CONFIG_SUCCESS';

export const ADD_FILTER_CUSTOM_ITEM_SUCCESS = 'ADD_FILTER_CUSTOM_ITEM_SUCCESS';
export const UPDATE_FILTER_CUSTOM_ITEM_SUCCESS = 'UPDATE_FILTER_CUSTOM_ITEM_SUCCESS';
export const DELETE_FILTER_CUSTOM_ITEM_SUCCESS = 'DELETE_FILTER_CUSTOM_ITEM_SUCCESS';
export const UPDATE_POSITION_FILTER_CUSTOM_ITEM_SUCCESS = 'UPDATE_POSITION_FILTER_CUSTOM_ITEM_SUCCESS';

export const UPDATE_FILTERS_POSITIONS = 'UPDATE_FILTERS_POSITIONS';

/**
 * Локальное обновление массива позиций смешанного списка items
 * (используется во время drag-drop до приёма от бэка).
 */
export const updateFiltersPositions = (payload) => ({
  type: UPDATE_FILTERS_POSITIONS,
  payload,
});

const fetchFiltersStart = () => ({
  type: FETCH_FILTERS_START,
});

const fetchFiltersSuccess = (payload) => ({
  type: FETCH_FILTERS_SUCCESS,
  payload,
});

const fetchFiltersFail = (error) => ({
  type: FETCH_FILTERS_FAIL,
  payload: error,
});

const createFilterStart = () => ({
  type: CREATE_FILTER_START,
});

const createFilterSuccess = (payload) => ({
  type: CREATE_FILTER_SUCCESS,
  payload,
});

const createFilterFail = (error) => ({
  type: CREATE_FILTER_FAIL,
  payload: error,
});

const deleteFilterStart = () => ({
  type: DELETE_FILTER_START,
});

const deleteFilterSuccess = (payload) => ({
  type: DELETE_FILTER_SUCCESS,
  payload,
});

const deleteFilterFail = (error) => ({
  type: DELETE_FILTER_FAIL,
  payload: error,
});

const fetchFilterItemStart = () => ({
  type: FETCH_FILTER_ITEM_START,
});

const fetchFilterItemSuccess = (payload) => ({
  type: FETCH_FILTER_ITEM_SUCCESS,
  payload,
});

const fetchFilterItemFail = (error) => ({
  type: FETCH_FILTER_ITEM_FAIL,
  payload: error,
});

const updateFilterStart = () => ({
  type: UPDATE_FILTER_START,
});

const updateFilterSuccess = (payload) => ({
  type: UPDATE_FILTER_SUCCESS,
  payload,
});

const updateFilterFail = (error) => ({
  type: UPDATE_FILTER_FAIL,
  payload: error,
});

const addFilterItemsSuccess = (payload) => ({
  type: ADD_FILTER_ITEMS_SUCCESS,
  payload,
});

const removeFilterItemSuccess = (payload) => ({
  type: REMOVE_FILTER_ITEM_SUCCESS,
  payload,
});

const updateFilterItemPositionSuccess = (payload) => ({
  type: UPDATE_FILTER_ITEM_POSITION_SUCCESS,
  payload,
});

const replaceFilterAttributeItemsSuccess = (payload) => ({
  type: REPLACE_FILTER_ATTRIBUTE_ITEMS_SUCCESS,
  payload,
});

const upsertFilterAttributeConfigSuccess = (payload) => ({
  type: UPSERT_FILTER_ATTRIBUTE_CONFIG_SUCCESS,
  payload,
});

const deleteFilterAttributeConfigSuccess = (payload) => ({
  type: DELETE_FILTER_ATTRIBUTE_CONFIG_SUCCESS,
  payload,
});

const addFilterCustomItemSuccess = (payload) => ({
  type: ADD_FILTER_CUSTOM_ITEM_SUCCESS,
  payload,
});

const updateFilterCustomItemSuccess = (payload) => ({
  type: UPDATE_FILTER_CUSTOM_ITEM_SUCCESS,
  payload,
});

const deleteFilterCustomItemSuccess = (payload) => ({
  type: DELETE_FILTER_CUSTOM_ITEM_SUCCESS,
  payload,
});

const updatePositionFilterCustomItemSuccess = (payload) => ({
  type: UPDATE_POSITION_FILTER_CUSTOM_ITEM_SUCCESS,
  payload,
});

/**
 * Загружает список всех фильтров для админки.
 * Используется на странице /filters и для синхронизации после CRUD-операций.
 */
export const fetchFilters = () => async (dispatch) => {
  dispatch(fetchFiltersStart());
  try {
    const response = await get('/filters');
    dispatch(fetchFiltersSuccess({ data: response.data }));
  } catch (e) {
    dispatch(fetchFiltersFail(e));
  }
};

/**
 * Загружает конкретный фильтр со всеми items и customItems.
 * Используется на странице редактирования.
 */
export const fetchFilterItem = (id) => async (dispatch) => {
  dispatch(fetchFilterItemStart());
  try {
    const response = await get(`/filters/${id}`);
    dispatch(fetchFilterItemSuccess({ data: response.data }));
    return response.data;
  } catch (e) {
    dispatch(fetchFilterItemFail(e));
    return null;
  }
};

/**
 * Создаёт новый фильтр.
 * Тосты success/fail общие. setNewItemProcess — UI-флаг для индикатора создания.
 */
export const createFilter = (body, translations, setNewItemProcess) => async (dispatch) => {
  dispatch(createFilterStart());
  try {
    const response = await post('/filters', body);
    dispatch(createFilterSuccess({ data: response.data }));
    if (typeof setNewItemProcess === 'function') {
      setNewItemProcess((prev) => prev.filter((elId) => elId !== body.identifier));
    }
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    return response.data;
  } catch (e) {
    dispatch(createFilterFail(e));
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    return null;
  }
};

/**
 * Обновляет основные поля фильтра (identifier / localizeInfos / scopeTypes).
 * Возвращает true/false для удобства caller'а.
 */
export const updateFilter = (id, body, translations) => async (dispatch) => {
  dispatch(updateFilterStart());
  try {
    await put(`/filters/${id}`, body);
    dispatch(updateFilterSuccess({ id, ...body }));
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    return true;
  } catch (e) {
    dispatch(updateFilterFail(e));
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    return false;
  }
};

/**
 * Удаляет фильтр со всеми items / customItems (cascade на бэке).
 */
export const deleteFilter = (id, translations, setDeleteProcess) => async (dispatch) => {
  dispatch(deleteFilterStart());
  try {
    await remove(`/filters/${id}`);
    dispatch(deleteFilterSuccess(id));
    if (typeof setDeleteProcess === 'function') {
      setDeleteProcess((prev) => prev.filter((elId) => elId !== id));
    }
    Store.addNotification(COMMON_DATA_DELETE_SUCCESS(translations));
    return true;
  } catch (e) {
    dispatch(deleteFilterFail(e));
    if (typeof setDeleteProcess === 'function') {
      setDeleteProcess((prev) => prev.filter((elId) => elId !== id));
    }
    Store.addNotification(COMMON_DATA_DELETE_FAIL(translations));
    return false;
  }
};

/**
 * Батчит добавление нескольких items в фильтр одним запросом.
 * items: Array<{ objectType, objectId, attributeValueId?, valueText? }>.
 * Бэк возвращает массив созданных filter-items — кладём в state.filter.filterItems.
 */
export const addFilterItems = (filterId, items, translations) => async (dispatch) => {
  try {
    const response = await post(`/filters/${filterId}/items`, { items });
    dispatch(addFilterItemsSuccess({ filterId, items: response.data }));
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    await dispatch(fetchFilterItem(filterId));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * «Выбрать всё» для одного scope фильтра. Шлёт один запрос
 * `POST /filters/:id/items/select-all` — бэк сам перечисляет ВСЕ сущности
 * указанного scope (включая неподгруженные на клиенте) и вставляет filter-items.
 * Это единственный масштабируемый путь: тянуть на клиент все id (товары могут
 * быть десятками тысяч) и постить обратно — неприемлемо.
 *
 * `body` — как минимум `{ objectType }`; для scope `attribute` дополнительно
 * `{ attributeSetId, attributeIdentifier }`, чтобы выбрать все значения
 * конкретного атрибута. Серверная часть эндпоинта реализуется отдельно.
 *
 * После успеха перезагружаем фильтр целиком (`fetchFilterItem`), чтобы
 * includedMap / tri-state в табах отразили новый полный набор items.
 */
export const selectAllFilterItems = (filterId, body, translations) => async (dispatch) => {
  try {
    const response = await post(`/filters/${filterId}/items/select-all`, body);
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    await dispatch(fetchFilterItem(filterId));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * Удаляет один item фильтра. Тосты — COMMON_DATA_DELETE_*.
 */
export const removeFilterItem = (filterId, itemId, translations) => async (dispatch) => {
  try {
    await remove(`/filters/${filterId}/items/${itemId}`);
    dispatch(removeFilterItemSuccess({ filterId, itemId }));
    Store.addNotification(COMMON_DATA_DELETE_SUCCESS(translations));
    return true;
  } catch (e) {
    Store.addNotification(COMMON_DATA_DELETE_FAIL(translations));
    throw e;
  }
};

/**
 * Массово удаляет items из фильтра. Под капотом — N параллельных DELETE через
 * Promise.allSettled, чтобы одно падение не блокировало остальные.
 *
 * Зачем отдельный action: при bulk-снятии чекбокса категории мы не хотим
 * показывать сотни одиночных тостов от removeFilterItem — поэтому шлём здесь
 * один сводный тост (success / partial / fail) и точечно диспатчим
 * REMOVE_FILTER_ITEM_SUCCESS только для реально удалённых items.
 *
 * Возвращает { succeeded, failed, total } для дальнейшей обработки caller'ом
 * (например, чтобы разблокировать локальный pending-set).
 */
export const removeFilterItemsBulk = (filterId, itemIds, translations) => async (dispatch) => {
  const ids = Array.isArray(itemIds) ? itemIds.filter((id) => id !== null && id !== undefined) : [];
  const total = ids.length;
  if (total === 0) {
    return { succeeded: 0, failed: 0, total: 0 };
  }
  const results = await Promise.allSettled(
    ids.map((itemId) => remove(`/filters/${filterId}/items/${itemId}`)),
  );
  let succeeded = 0;
  results.forEach((res, idx) => {
    if (res.status === 'fulfilled') {
      succeeded += 1;
      dispatch(removeFilterItemSuccess({ filterId, itemId: ids[idx] }));
    } else {
      // Лог нужен — partial-fail сложно дебажить без него.
      console.error('removeFilterItemsBulk fail:', ids[idx], res.reason);
    }
  });
  const failed = total - succeeded;
  if (failed === 0) {
    Store.addNotification(COMMON_DATA_DELETE_SUCCESS(translations));
  } else if (succeeded === 0) {
    Store.addNotification(COMMON_DATA_DELETE_FAIL(translations));
  } else {
    const failTemplate = COMMON_DATA_DELETE_FAIL(translations);
    Store.addNotification({
      ...failTemplate,
      message: `${succeeded} / ${total} ${translations?.common_delete_success || ''}`.trim(),
    });
  }
  return { succeeded, failed, total };
};

/**
 * Массово переносит выделенные узлы (и их поддеревья) под нового родителя одним
 * запросом. `payload` = { items: [{ objectId, objectType }], newParentId,
 * newParentType } — уже собран вызывающим. Бэк переносит атомарно; после успеха
 * перечитываем дерево целиком (у нескольких узлов сменились parent_id/positionLex).
 */
export const reparentFilterItemsBulk = (filterId, payload, translations) => async (dispatch) => {
  try {
    const response = await put(`/filters/${filterId}/items/reparent`, payload);
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    await dispatch(fetchFilterItem(filterId));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * Обновляет позицию item фильтра (drag-drop).
 * Тосты не показываем — операция типично пакетная.
 * body — UpdatePositionWithParentDto { newParentId, position: { leftObjectId, leftObjectType, rightObjectId, rightObjectType } }.
 */
export const updateFilterItemPosition = (filterId, itemId, body) => async (dispatch) => {
  try {
    const response = await put(`/filters/${filterId}/items/${itemId}/position`, body);
    dispatch(updateFilterItemPositionSuccess({ filterId, itemId, item: response.data }));
    return response.data;
  } catch (e) {
    // Лог нужен — позиции «дёргаются» если запрос упал.
    console.error('updateFilterItemPosition error:', e);
    throw e;
  }
};

/**
 * Создаёт кастомный пункт фильтра (title + value, без привязки к сущности).
 */
export const addFilterCustomItem = (filterId, body, translations) => async (dispatch) => {
  try {
    const response = await post(`/filters/${filterId}/custom-items`, body);
    dispatch(addFilterCustomItemSuccess({ filterId, item: response.data }));
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * Обновляет существующий кастомный пункт фильтра (title / value).
 */
export const updateFilterCustomItem = (filterId, itemId, body, translations) => async (dispatch) => {
  try {
    const response = await put(`/filters/${filterId}/custom-items/${itemId}`, body);
    dispatch(updateFilterCustomItemSuccess({ filterId, itemId, item: response.data }));
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * Удаляет кастомный пункт фильтра.
 */
export const deleteFilterCustomItem = (filterId, itemId, translations) => async (dispatch) => {
  try {
    await remove(`/filters/${filterId}/custom-items/${itemId}`);
    dispatch(deleteFilterCustomItemSuccess({ filterId, itemId }));
    Store.addNotification(COMMON_DATA_DELETE_SUCCESS(translations));
    return true;
  } catch (e) {
    Store.addNotification(COMMON_DATA_DELETE_FAIL(translations));
    throw e;
  }
};

/**
 * Атомарно заменяет все attribute-items конкретной пары
 * `(attributeSetId, attributeIdentifier)` в фильтре. Зовёт
 * `PUT /filters/:id/items/attribute/replace` — на бэке транзакция
 * DELETE+INSERT. Используется при переключении режима карточки атрибута
 * между «Галочки» и «Диапазон» (см. AC #8 спецификации).
 *
 * `items` — массив новых записей (с `objectType='attribute'`), может быть
 * пустым (тогда просто очистка). Каждый элемент соответствует тому же
 * формату, что и `addFilterItems`, но дополнительно поддерживает поля
 * range-режима: `isRange`, `rangeFrom`, `rangeTo`, `allowedProductStatusIds`.
 *
 * Возвращает свежий массив filter-items от бэка (новые ids), который
 * подменяет в reducer'е элементы данной пары.
 */
export const replaceAttributeFilterItems = (filterId, body, translations) => async (dispatch) => {
  try {
    const response = await put(`/filters/${filterId}/items/attribute/replace`, body);
    dispatch(replaceFilterAttributeItemsSuccess({
      filterId,
      attributeSetId: Number(body.attributeSetId),
      attributeIdentifier: body.attributeIdentifier ?? '',
      items: response.data,
    }));
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * Апсертит запись конфига атрибута фильтра — пару `(attributeSetId,
 * attributeIdentifier)` в `filters.config->attribute`. Зовёт
 * `PUT /filters/:id/config/attribute`.
 *
 * Два сценария:
 *  - регистрация пары при выборе набора + атрибута (без `isPinned` в body):
 *    карточка восстанавливается из конфига, а не из items;
 *  - переключение закрепления (`isPinned: boolean`): бэк снимает/чистит
 *    baseline `knownValues`. Пин НЕ выделяет существующие значения — он лишь
 *    меняет иконку; появившиеся позже значения авто-добавляются синком.
 *
 * Обновляем локальный state оптимистично через
 * `UPSERT_FILTER_ATTRIBUTE_CONFIG_SUCCESS` из ответа бэка (актуальная запись
 * конфига). НЕ перезагружаем items — пин по требованию не должен менять
 * выборку значений (attribute-pin-issue2).
 */
export const upsertFilterAttributeConfig = (filterId, body, translations) => async (dispatch) => {
  try {
    const response = await put(`/filters/${filterId}/config/attribute`, body);
    dispatch(upsertFilterAttributeConfigSuccess({
      filterId,
      entry: response.data,
    }));
    Store.addNotification(COMMON_DATA_UPDATE_SUCCESS(translations));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_UPDATE_FAIL(translations));
    throw e;
  }
};

/**
 * Удаляет запись конфига атрибута фильтра (снятие регистрации карточки).
 * Зовёт `DELETE /filters/:id/config/attribute` с `{ attributeSetId,
 * attributeIdentifier }`. Материализованные items бэк НЕ трогает — карточка
 * лишь перестаёт восстанавливаться из конфига.
 */
export const deleteFilterAttributeConfig = (filterId, body, translations) => async (dispatch) => {
  try {
    await remove(`/filters/${filterId}/config/attribute`, { data: body });
    dispatch(deleteFilterAttributeConfigSuccess({
      filterId,
      attributeSetId: Number(body.attributeSetId),
      attributeIdentifier: body.attributeIdentifier ?? '',
    }));
    Store.addNotification(COMMON_DATA_DELETE_SUCCESS(translations));
    return true;
  } catch (e) {
    Store.addNotification(COMMON_DATA_DELETE_FAIL(translations));
    throw e;
  }
};

/**
 * «Снять всё» для одного scope фильтра — зеркало {@link selectAllFilterItems}.
 * Шлёт `POST /filters/:id/items/deselect-all`; бэк удаляет все filter-items
 * указанного scope (для `attribute` — пары `attributeSetId` +
 * `attributeIdentifier`). После успеха перезагружаем фильтр, чтобы табы
 * отразили опустевший набор.
 */
export const deselectAllFilterItems = (filterId, body, translations) => async (dispatch) => {
  try {
    const response = await post(`/filters/${filterId}/items/deselect-all`, body);
    Store.addNotification(COMMON_DATA_DELETE_SUCCESS(translations));
    await dispatch(fetchFilterItem(filterId));
    return response.data;
  } catch (e) {
    Store.addNotification(COMMON_DATA_DELETE_FAIL(translations));
    throw e;
  }
};

/**
 * Обновляет позицию кастомного пункта фильтра (drag-drop).
 * Тосты не показываем — пакетная операция.
 */
export const updateFilterCustomItemPosition = (filterId, itemId, body) => async (dispatch) => {
  try {
    const response = await put(`/filters/${filterId}/custom-items/${itemId}/position`, body);
    dispatch(updatePositionFilterCustomItemSuccess({ filterId, itemId, item: response.data }));
    return response.data;
  } catch (e) {
    console.error('updateFilterCustomItemPosition error:', e);
    throw e;
  }
};
